"use client";
import { NextLink } from "@components/ui/next-link/NextLink";
import type { IPagePath } from "@contentful-api/types/contentful";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { DEFAULT_ATTRIBUTES_TO_RETRIEVE } from "@lib/algolia/constants";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import type { AlgoliaItem } from "@lib/analytics/analyticsTypes";
import { AlgoliaProductProperties } from "@lib/wrappers/algolia/types";
import hexRgb from "hex-rgb";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { ProductCarousel } from "./ProductCarousel";

interface ISearchProductsHitsToCarouselProps {
    products?: string[];
    headline?: string;
    assistiveMessage?: string;
    sortingOrder?: string[];
    carouselType?: string;
    extraLink?: IPagePath;
    extraLinkLabel?: string;
    id?: string;
    theme?: string;
}

export const SearchProductsHitsToCarousel = (props: ISearchProductsHitsToCarouselProps) => {
    const {
        products,
        headline,
        assistiveMessage,
        sortingOrder,
        carouselType,
        extraLink = null,
        extraLinkLabel = null,
        theme,
    } = props;

    const locale = useLocale();
    const [hits, setHits] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldFetch, setShouldFetch] = useState(false);
    const hasFetched = useRef(false);
    const ref = useRef<HTMLDivElement>(null);

    const { browseClient } = useAlgoliaClient(locale);
    const filters = products?.map((id) => `objectID:${id}`).join(" OR ") ?? "";

    // Intersection observer to trigger fetch
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasFetched.current) {
                    setShouldFetch(true);
                    hasFetched.current = true;
                }
            },
            {
                rootMargin: "50%",
            }
        );

        if (ref.current) observer.observe(ref.current);

        return () => {
            if (ref.current) observer.unobserve(ref.current);
        };
    }, []);

    // Actual data fetching
    useEffect(() => {
        if (!shouldFetch || !filters) {
            return;
        }

        setIsLoading(true);

        browseClient
            .browseObjects({
                filters: filters,
                facetFilters: ["hasStock:true"],
                attributesToRetrieve: [
                    ...DEFAULT_ATTRIBUTES_TO_RETRIEVE,
                    AlgoliaProductProperties.descriptiveHeading,
                ],
                batch: (batch) => {
                    const normalizedHits = batch.map((hit, index) => ({
                        ...hit,
                        id: hit.id ?? hit.objectID,
                        productColors: Array.isArray(hit.productColors)
                            ? hit.productColors.map((it) => ({
                                  priceWithDiscount: it.priceRaw,
                                  priceWithoutDiscount: it.listPriceRaw,
                                  image: it.image,
                                  objectID: it.objectID,
                                  colorCode: it.colorCode,
                                  discountPercentage: it.discountPercentage,
                                  hasDiscount: it.hasDiscount,
                                  eaBadge: it?.eaBadge,
                                  hasStock: it.hasStock,
                                  lowestPrice: it.lowestPriceRaw,
                                  discountPercentageLowestPrice: it.discountPercentageLowestPrice,
                              }))
                            : hit.productColors,
                        queryID: hit.__queryID,
                        index,
                        productName: hit.name,
                        priceWithoutDiscount: hit.listPriceRaw,
                        priceWithDiscount: hit.priceRaw,
                        lowestPrice: hit.lowestPriceRaw,
                        discountPercentageLowestPrice: hit.discountPercentageLowestPrice,
                        backgroundColor: hexRgb("FFF"),
                    }));

                    if (sortingOrder?.length) {
                        normalizedHits.sort(
                            (a, b) =>
                                sortingOrder.findIndex((it) => it === a.objectID) -
                                sortingOrder.findIndex((it) => it === b.objectID)
                        );
                    }

                    setHits(normalizedHits);
                },
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [shouldFetch, filters, browseClient, sortingOrder]);

    // Analytics
    useEffect(() => {
        if (!hits.length) {
            return;
        }

        const analyticsTimeout = setTimeout(() => {
            AnalyticsClient.loadItemsList(hits as unknown as AlgoliaItem[], {
                listName: carouselType,
            });
        }, 3000);
        return () => {
            clearTimeout(analyticsTimeout);
        };
    }, [hits]);

    const ExtraLinkComponent = () => {
        const { completeSlug, label } = extraLink?.fields ?? {};
        if (completeSlug) {
            return <NextLink href={completeSlug}>{extraLinkLabel ?? label}</NextLink>;
        }
        return null;
    };

    return (
        <div ref={ref}>
            <ProductCarousel
                theme={theme}
                slideData={hits}
                isLoading={isLoading}
                headline={headline}
                assistiveMessage={assistiveMessage}
                carouselType={carouselType}
                extraLink={<ExtraLinkComponent />}
                carouselHeight={["auto", "100vw", "68vw", "52vw", "49vw"]}
                tileWidth={[
                    "calc(100vw/1.3)",
                    "calc(100vw/2.5)",
                    "calc(100vw/2.5)",
                    "calc(100vw/3.5)",
                ]}
                slidesPerView={[1.3, 2.5, 3.5]}
            />
        </div>
    );
};
